/**
 * Every query the app issues, by name.
 *
 * The browser never sends a query. It names one of these and passes arguments; the demo's Worker
 * validates the arguments, builds the query here, and asks r2db for a lease on exactly that query.
 * The browser then watches the lease over r2db's socket, so it can read what these return and
 * nothing else — the account token never leaves the Worker. The browser builds the same query from
 * the same name and arguments, because folding the live stream needs its shape.
 *
 * The shapes are the tracker's: a list row is an issue with its owner, its tags and a count of its
 * comments; the detail pane is one issue with its whole thread; the activity feed is the newest
 * comments with their author and issue.
 */
import { defineRelationships, exists, fieldCondition, ilike, inList, newQueryBuilder, rel } from 'r2db';
import type { Cond } from 'r2db';

import { comment, issue, ISSUE_PRIORITIES, ISSUE_STATUSES, project, schema, tag, user } from './schema.ts';
import type { IssuePriority, IssueStatus } from './schema.ts';

export const q = newQueryBuilder(schema);

export const rels = defineRelationships({
  issueProject: rel(issue, project, { projectId: 'id' }),
  issueOwner: rel(issue, user, { ownerId: 'id' }),
  issueTags: rel(issue, tag, { id: 'issueId' }),
  issueComments: rel(issue, comment, { id: 'issueId' }),
  commentAuthor: rel(comment, user, { authorId: 'id' }),
  commentIssue: rel(comment, issue, { issueId: 'id' }),
});

/** How far a window grows per "load more", and how far it may grow at all. */
export const PAGE_SIZE = 50;
export const MAX_WINDOW = 500;
/**
 * The switcher holds every project, so this is a ceiling on the demo and not on the shape.
 *
 * It has to stay ABOVE the base's project count or the switcher silently shows an alphabetical
 * prefix of it — and worse than silently: `App.tsx`'s fallback asks whether the injected
 * `DEFAULT_PROJECT` is in the list and reroutes to `projects[0]` when it is not, so a truncation
 * reads as a routing bug rather than as a limit. 4,000 clears the ~2,710 projects a 100 GB base
 * holds at `ISSUES_PER_PROJECT = 10_000`.
 */
export const PROJECT_LIMIT = 4000;
/** A board column is a window per status; it shows this many and says "+" past it. */
export const COLUMN_LIMIT = 25;
export const FEED_LIMIT = 30;

export const FILTER_AXES = ['title', 'status', 'priority', 'owner', 'tag', 'comment'] as const;
export type SearchAxis = (typeof FILTER_AXES)[number];
export interface FilterTerm { axis: SearchAxis; value: string }
export type IssueFilter = readonly FilterTerm[];
/** The most terms one search takes. Exported because the box enforces it too — a seventh term is
 *  a refusal on the wire, and a search box that lets you type one is a search box that lies. */
export const MAX_TERMS = 6;
const MAX_TERM_LENGTH = 60;

type IssueRow = typeof issue extends { [k: symbol]: infer _ } ? any : any;
const contains = <R>(column: string, value: string): Cond<R> => fieldCondition(column, ilike(`%${value}%`)) as Cond<R>;

/**
 * One column, one condition: `=` for a single value, `IN` for several.
 *
 * The two are not interchangeable, which is why this helper exists rather than every equality axis
 * simply spelling `inList`. r2db leases a query **family**, and a family is formed by punching
 * every root-level `column = literal` out of the AST into a parameter (`familyKey` in
 * `r2db/runtime`), so the board's five columns and every project's list are partitions of ONE
 * pipeline instead of a pipeline each. An `IN` is not holeable — it stays in the template — so a
 * shape that spells its only value that way gets its own pipeline, and in a 128 MiB object the
 * per-query operator state is the cost that matters. The single value therefore keeps its bind,
 * and only a genuine several-of-them pays for a residual condition.
 */
const oneOf = <R>(column: string, values: readonly string[]): Cond<R> =>
  fieldCondition(column, values.length === 1 ? values[0] : inList([...values])) as Cond<R>;

/** The three axes that are an equality on one of the issue's own columns, and the column each
 *  names. These are the ones `issueConditions` has to fold; `title`, `tag` and `comment` are a
 *  substring or an `EXISTS`, which no family binds and which may repeat freely. */
const EQUALITY_COLUMN = { status: 'status', priority: 'priority', owner: 'ownerId' } as const;

/**
 * The search terms — and, for a board column, the status that column stands for — as conditions on
 * the issue, within a project the caller has already pinned.
 *
 * Nothing here is a seek, and it does not need to be: the project's `where` in front of it is what
 * bounds the work, so each of these is a predicate applied while one project's contiguous slice
 * streams past. `schema.ts`'s header has the argument and the numbers.
 *
 * **One condition per column, which is a requirement and not a tidiness rule.** Two equalities on
 * one column become two family parameters with one name, and the build is refused outright:
 * `query family does not build: unsupported condition: a query family cannot bind the same column
 * twice`. It is refused on the only path a page has, so the result is not a wrong view but a
 * *frozen* one — `useLive` keeps the previous rows up while a new query settles, and a query that
 * never settles leaves them up for good, under a search box that says it is filtering. Two ways in,
 * both of which a visitor had:
 *
 *   - **`status:<x>` with the BOARD open.** Each column ANDs the status it stands for onto the
 *     search, so a single status term made all FIVE columns fail to build — Review's included —
 *     while the list beside it, one binding, answered fine.
 *   - **two terms on one equality axis, in either view**: `status:review status:done`,
 *     `priority:urgent priority:low`, `owner:ada owner:li`. The box offers them and ANDs them.
 *
 * So an equality column's values are collected and emitted **once**, through {@link oneOf}. ANDing
 * them was never a meaning anyone wanted — no issue's status is both `review` and `done` — so the
 * only sensible reading of two of them is "either", which is what the `IN` says. The other three
 * axes keep one condition per term, ANDed, because "both of these tags" *is* a thing to want.
 *
 * **The board's column intersects, where the search unions.** A column shows what is in that
 * column, so its status narrows the search's statuses rather than joining them: `status:review`
 * with the board open asks Review for `review` — one value, still a bind — and would ask Done for
 * nothing. `Board.tsx` renders only the columns the search admits, so it does not ask; a lease that
 * asks anyway gets the empty `IN`, which is an empty result rather than a refusal.
 *
 * **`owner` and `tag` take an EXACT id**, not a substring, because the search box's typeahead has
 * one: it offers the cast and the tag vocabulary by name and sends what was chosen. That turns each
 * row's inner check into a comparison rather than a substring match, and — if the planner's cost
 * model decides to drive from the child rather than the parent — makes the child side a seek too.
 * It used to be `ilike('%v%')` over `id` OR `name`, which could not seek either way.
 *
 * **`owner` is a predicate on the issue's own column, not an `EXISTS`.** The issue row already
 * carries `ownerId`, and `issueOwner` correlates it to the user's own primary key, so
 * `exists(issueOwner, u => u.where(id = v))` and `ownerId = v` select exactly the same rows
 * whenever user `v` exists — which is the only thing the picker offers. The `EXISTS` form was not
 * merely slower: on the deployed 100 GB base it **did not finish**, giving up after 64 rounds with
 * a page still wanted, for most owners and most projects.
 *
 * The cause was not the subquery's own work, which is a primary-key lookup into a 120-row table.
 * The planner flipped the join — a 120-row table looks cheap to drive from — and the flipped
 * parent probe read `issue_by_owner`, which is cross-project. One owner's slice of that index runs
 * newest project first, so a low-numbered project's rows sit ~99% of the way through the 226,000
 * this owner has, and the window cannot fill inside the object's leaf cache.
 *
 * The general rule, worth knowing before adding an axis: an `EXISTS` whose child predicate is on
 * the correlation's own child field says nothing the parent's correlation column does not already
 * say. `tag` and `comment` are NOT reducible this way — those are separate rows keyed by `issueId`,
 * so the subquery is the only thing that can answer them.
 *
 * **The project's `where` leads, and on an older engine that was load-bearing rather than tidy.**
 * The paged leaf used to decide between `issue_by_project_created` and `issue_by_owner` — each
 * seeking one pinned column, each streaming the order — on whichever equality a push guard
 * happened to lift out of the condition, which is the first one written. So `owner:` ahead of the
 * partition read the cross-project index and did not finish, with the same rows either way. The
 * leaf now breaks that tie on the counted rows per seek key (10,000 per project against 226,000
 * per owner — `rindle-paged`'s `declared_match`, design 439 §4.4), so clause order is no longer a
 * planning input. The order is kept anyway: a deployed store runs the engine it was deployed with.
 */
function issueConditions(filter: IssueFilter, status?: IssueStatus): Cond<IssueRow>[] {
  const conds: Cond<IssueRow>[] = [];
  const valuesFor = (axis: SearchAxis) => [...new Set(filter.filter(t => t.axis === axis).map(t => t.value))];

  // `status` can arrive twice over, and the two mean different things: the board column's narrows,
  // the search's choose among themselves. The query admits their intersection, so a column the
  // search excludes asks for the empty set rather than for a contradiction.
  const searched = valuesFor('status');
  const statuses = status ? (searched.length ? searched.filter(s => s === status) : [status]) : searched;
  if (status || searched.length) conds.push(oneOf(EQUALITY_COLUMN.status, statuses));
  for (const axis of ['priority', 'owner'] as const) {
    const values = valuesFor(axis);
    if (values.length) conds.push(oneOf(EQUALITY_COLUMN[axis], values));
  }
  // Then the axes no family binds, each term its own condition, in the order they were typed.
  // A `switch` rather than three `if`s so that an axis added to `FILTER_AXES` and forgotten here
  // fails to COMPILE: the mistake it would otherwise make is a filter term that filters nothing,
  // which looks exactly like a search that matched everything.
  for (const term of filter) {
    switch (term.axis) {
      case 'title': conds.push(contains('title', term.value)); break;
      case 'tag': conds.push(exists(rels.issueTags, t => t.where(fieldCondition('name', term.value))) as Cond<IssueRow>); break;
      case 'comment': conds.push(exists(rels.issueComments, c => c.where(contains('body', term.value))) as Cond<IssueRow>); break;
      // Folded above, one condition per column.
      case 'status': case 'priority': case 'owner': break;
      default: { const unhandled: never = term.axis; throw new TypeError(`no condition for search axis ${String(unhandled)}`); }
    }
  }
  return conds;
}

/** What a list row and a board card show: the issue, its owner, its tags and how many comments. */
const card = <Q extends ReturnType<typeof q.issue.orderBy>>(query: Q) =>
  query
    .sub('project', rels.issueProject, p => p)
    .sub('owner', rels.issueOwner, u => u)
    .sub('tags', rels.issueTags, t => t.orderBy('name', 'asc'))
    .countAs('commentCount', rels.issueComments);

export interface IssuesArgs { project: string; limit: number; filter: IssueFilter; status?: IssueStatus }
export interface IssueCountArgs { project: string }
export interface DetailArgs { id: string }
export interface FeedArgs { limit: number }

export const QUERIES = {
  /**
   * One project's list — and, with `status`, one of its board columns: newest first, a growing
   * window.
   *
   * `project` first in the `where`, which is not cosmetic: it is the leading column of both
   * `issue_by_project_created` and `issue_by_project_status_created`, so it is the seek prefix,
   * and everything after it is applied to rows that are already one project's contiguous slice.
   * It is REQUIRED rather than optional for the same reason — an optional partition is one a
   * caller can forget, and the shape that forgets it is a scan of the store.
   */
  issues: ({ project, limit, filter, status }: IssuesArgs) => {
    let query = q.issue
      .where(fieldCondition('projectId', project) as Cond<IssueRow>)
      .orderBy('createdAt', 'desc').orderBy('id', 'asc');
    for (const cond of issueConditions(filter, status)) query = query.where(cond);
    return card(query.limit(limit));
  },
  /**
   * How many issues a project holds — the number behind the window, which the window itself
   * cannot tell you because it stops at its limit.
   *
   * **It takes nothing but the project, and that is the cost guarantee rather than an omission.**
   * A count is answered off the B+tree's counted branch pages — O(tree height), not O(rows) —
   * only when the predicate is *exactly* a set of equality pins forming a prefix of an index that
   * already exists (design 463 §3.6, conditions 1–8). `projectId` is prefix 1 of
   * `issue_by_project_created`, so this shape is free. Anything else in the predicate makes the
   * engine fold the rows instead: a second pin that is not the index's next column, an `IN`, a
   * range, an `ILIKE`, an `EXISTS`. Measured on a local 120k-issue store, 60k per project: this
   * shape is 3.0 ms against a 3.6 ms round-trip floor — counting 60,000 rows costs what counting
   * 2 costs — while `+ priority=urgent` is 14.2 ms, `+ status IN (2)` 19.4 ms and
   * `+ title ILIKE` 30.4 ms, each scanning the project's whole slice.
   *
   * So the arguments cannot express the expensive shape. A caller that wants a count under a
   * search has to ask for one that does not exist, rather than get one that quietly scans — the
   * same reason `issues` makes `project` required (and the same reason `explain` cannot be the
   * guard here: it answered `covered` for the 14.2 ms shape, because it asks whether an index
   * serves the seek and the order, not whether the pins exhaust the predicate).
   *
   * `(projectId, status)` is free in the same way — prefix 2 of
   * `issue_by_project_status_created` — and the board's column headers were built on it before
   * being taken back out. Adding a `status` argument here is all it would take to have them back.
   *
   * One `{ count }` row, live like anything else: a write that lands in the counted range moves
   * it. A root aggregate takes no `limit` or `orderBy` — the engine rejects that pairing — which
   * is why this is a query of its own beside the window it labels.
   */
  issueCount: ({ project }: IssueCountArgs) =>
    q.issue.where(fieldCondition('projectId', project) as Cond<IssueRow>).count(),
  /** Every project, alphabetized: the switcher. Bounded by `PROJECT_LIMIT`. */
  projects: () => q.project.orderBy('name', 'asc').limit(PROJECT_LIMIT),
  /** One issue with its whole thread, oldest first; the earliest comment is the description. */
  detail: ({ id }: DetailArgs) =>
    q.issue.where.id(id)
      .sub('project', rels.issueProject, p => p)
      .sub('owner', rels.issueOwner, u => u)
      .sub('tags', rels.issueTags, t => t.orderBy('name', 'asc'))
      .sub('comments', rels.issueComments, c => c.orderBy('createdAt', 'asc').sub('author', rels.commentAuthor, u => u))
      .one(),
  /** The newest comments across every issue, each with its author and the issue it is on. */
  feed: ({ limit }: FeedArgs) =>
    q.comment.orderBy('createdAt', 'desc').orderBy('id', 'asc').limit(limit)
      .sub('author', rels.commentAuthor, u => u)
      .sub('issue', rels.commentIssue, i => i.sub('project', rels.issueProject, p => p)),
  /** The whole cast, for the user switcher and the owner picker. Small by construction. */
  users: () => q.user.orderBy('name', 'asc').limit(200),
} as const;
export type QueryName = keyof typeof QUERIES;

const isInt = (v: unknown, lo: number, hi: number): v is number => Number.isInteger(v) && (v as number) >= lo && (v as number) <= hi;
const isShort = (v: unknown): v is string => typeof v === 'string' && v.length > 0 && v.length <= MAX_TERM_LENGTH;

/**
 * Check a browser's arguments for a named query, or say what is wrong with them. The Worker runs
 * this before it builds anything: the arguments are untrusted, and a limit or a filter is what
 * decides how much work one lease asks r2db to keep doing.
 */
export function parseArgs(name: string, raw: unknown): { name: QueryName; args: any } {
  const a = (raw ?? {}) as Record<string, unknown>;
  switch (name) {
    case 'issues': {
      // The partition, checked like any other argument. A missing or malformed `project` is a
      // refusal and never a widening: the whole cost argument rests on this being pinned, so the
      // shape that would scan the store must not be reachable from the wire.
      if (!isShort(a.project)) throw new TypeError('project must be a short string');
      if (!isInt(a.limit, 1, MAX_WINDOW)) throw new TypeError(`limit must be an integer from 1 to ${MAX_WINDOW}`);
      const filter = a.filter ?? [];
      if (!Array.isArray(filter) || filter.length > MAX_TERMS) throw new TypeError(`filter must be at most ${MAX_TERMS} terms`);
      for (const t of filter as FilterTerm[]) {
        if (!t || !FILTER_AXES.includes(t.axis) || !isShort(t.value)) throw new TypeError('each filter term is {axis, value} with a known axis and a short value');
        if (t.axis === 'status' && !ISSUE_STATUSES.includes(t.value as IssueStatus)) throw new TypeError('unknown status');
        if (t.axis === 'priority' && !ISSUE_PRIORITIES.includes(t.value as IssuePriority)) throw new TypeError('unknown priority');
      }
      if (a.status !== undefined && !ISSUE_STATUSES.includes(a.status as IssueStatus)) throw new TypeError('unknown status');
      return { name, args: { project: a.project, limit: a.limit, filter: (filter as FilterTerm[]).map(t => ({ axis: t.axis, value: t.value })), ...(a.status ? { status: a.status } : {}) } };
    }
    case 'issueCount':
      // The same refusal as `issues`, for the same reason: an unpinned count is a count of the
      // store. There is deliberately nothing else to validate — see the query's own note.
      if (!isShort(a.project)) throw new TypeError('project must be a short string');
      return { name, args: { project: a.project } };
    case 'detail':
      if (!isShort(a.id)) throw new TypeError('id must be a short string');
      return { name, args: { id: a.id } };
    case 'feed':
      if (!isInt(a.limit, 1, FEED_LIMIT)) throw new TypeError(`limit must be an integer from 1 to ${FEED_LIMIT}`);
      return { name, args: { limit: a.limit } };
    case 'users':
    case 'projects':
      return { name, args: {} };
    default:
      throw new TypeError(`unknown query ${JSON.stringify(name)}`);
  }
}

export function buildQuery(name: QueryName, args: any) {
  return (QUERIES[name] as (args: any) => any)(args);
}

/**
 * The one key a live query is known by, everywhere.
 *
 * `useLive` keys its subscription registry with it, and the Worker uses it to say WHICH query the
 * lease it inlined into the document is for. Those two must agree character for character — a
 * key built in two places is a key that silently stops matching the first time an argument is
 * added or reordered, and the symptom would be the inlined lease going unused with nothing
 * anywhere saying so. One function, imported by both; `test/shared.test.ts` pins the agreement.
 */
export function liveKey(name: QueryName, args: unknown): string {
  return JSON.stringify([name, args ?? {}]);
}

/**
 * What a first paint waits for: the newest page of one project, unfiltered.
 *
 * This is `IssueList`'s first render — `{ project, limit: PAGE_SIZE, filter: [] }` — named here
 * because the Worker has to mint a lease for exactly it before the page has run any code. The
 * property ORDER is part of the contract, since `liveKey` is `JSON.stringify`.
 */
export function firstPaintQuery(project: string): { name: QueryName; args: { project: string; limit: number; filter: IssueFilter } } {
  return { name: 'issues', args: { project, limit: PAGE_SIZE, filter: [] } };
}
